import { FC, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import cn from 'classnames';
import {
  Button,
  createBemBlockBuilder,
  InPageNotification,
} from '@ebsco-ui/ebsco-ui';

import { AuthPageWrapper, PageSpinner, TextField } from '@app/components';
import { sharedMessages } from '@app/translations';

import { useResetPinStep1Mutation } from '../resetPinMutations';
import { StepsBar } from '../StepsBar';

import '@app/components/AuthPageWrapper/AuthPageWrapper.scss';

const cnBem = createBemBlockBuilder(['authPageWrapper']);

export const EnterBarcodePage: FC = () => {
  const { $t } = useIntl();
  const resetPinStep1Mutation = useResetPinStep1Mutation();

  const successNotification = useMemo(
    () =>
      resetPinStep1Mutation.isSuccess
        ? {
            variant: InPageNotification.VARIANT.success,
            header: $t({
              id: 'resetPin.success',
              defaultMessage: 'Success.',
            }),
            description: $t({
              id: 'resetPin.emailWasSent',
              defaultMessage:
                'An email has been sent to you with a link to reset your PIN.',
            }),
          }
        : undefined,
    [resetPinStep1Mutation.isSuccess, $t]
  );
  const errorNotification = useMemo(
    () =>
      resetPinStep1Mutation.isError
        ? {
            variant: InPageNotification.VARIANT.error,
            header: $t(sharedMessages.somethingWentWrong),
            description: $t(sharedMessages.weAreNotAbleToProcessRequest),
          }
        : undefined,
    [resetPinStep1Mutation.isError, $t]
  );
  const {
    handleSubmit,
    formState: { isSubmitting, isValid, isDirty },
    control,
  } = useForm({ mode: 'onTouched' });

  return (
    <>
      <Helmet>
        <title>
          {$t({
            id: 'resetPin.documentTitle',
            defaultMessage: 'Reset PIN - EBSCO Locate',
          })}
        </title>
      </Helmet>
      <AuthPageWrapper
        title={$t(sharedMessages.resetPin)}
        containerClassName={cnBem('__resetPinContainer')}
        notification={successNotification ?? errorNotification}
      >
        <form
          className={cn(cnBem('__form'), cnBem('__resetPinForm'))}
          onSubmit={handleSubmit(({ barcode }) => {
            resetPinStep1Mutation.mutate(barcode);
          })}
        >
          {resetPinStep1Mutation.isLoading && (
            <PageSpinner className={cnBem('__spinner')} />
          )}
          <StepsBar stepsAmount={3} currentStepIndex={0} />
          <div className={cn(cnBem('__row'), cnBem('__rowMessage'))}>
            {$t({
              id: 'resetPin.enterBarcode',
              defaultMessage:
                'To reset your PIN please enter the barcode associated with your account.',
            })}
          </div>
          <div className={cnBem('__row')}>
            <TextField
              label={$t(sharedMessages.barcode)}
              name="barcode"
              control={control}
              rules={{ required: $t(sharedMessages.required) }}
            />
          </div>
          <div className={cnBem('__row')}>
            <Button
              className={cnBem('__submitButton')}
              styleType="solid"
              type="submit"
              disabled={!isValid || isSubmitting || !isDirty}
            >
              {$t(sharedMessages.submit)}
            </Button>
          </div>
        </form>
      </AuthPageWrapper>
    </>
  );
};
